// src/controlsil/gestionNodoSIL.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import GridLayout from "react-grid-layout/legacy";
import { Global } from "../helpers/Global";

import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

// Toast
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

// Icons (MUI)
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import DeleteIcon from "@mui/icons-material/Delete";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import CloseIcon from "@mui/icons-material/Close";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import HowToRegIcon from "@mui/icons-material/HowToReg";

export const GestionNodoSIL = () => {
  const toast = useToast();

  // =========================
  // Tamaño fijo tarjetas
  // =========================
  const W_FIJO = 1;
  const H_FIJO = 2;

  // =========================
  // State
  // =========================
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");
  const [errorLienzo, setErrorLienzo] = useState(null);

  const [nodos, setNodos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  const [layoutDirty, setLayoutDirty] = useState(false);
  const [configDirty, setConfigDirty] = useState(false);

  const [configSIL, setConfigSIL] = useState({
    cols_canvas: 120,
    filas_canvas: 40,
    col_px: 50,
    row_height: 60,
  });

  // Ausencias activas
  const [ausenciasActivas, setAusenciasActivas] = useState([]);
  const [ausenciasLoading, setAusenciasLoading] = useState(false);

  // Dialogs
  const [dlgCrear, setDlgCrear] = useState(false);
  const [dlgEditar, setDlgEditar] = useState(false);
  const [dlgAsignar, setDlgAsignar] = useState(false);

  const [nodoSeleccionado, setNodoSeleccionado] = useState(null);

  // Form
  const [formTitulo, setFormTitulo] = useState("");
  const [formEtiqueta, setFormEtiqueta] = useState("");

  // Asignar
  const [usuarioAsignarId, setUsuarioAsignarId] = useState(""); // string
  const usuarioAsignarObj = useMemo(() => {
    const idNum = Number(usuarioAsignarId);
    if (!idNum) return null;
    return (usuarios || []).find((u) => Number(u.id) === idNum) || null;
  }, [usuarioAsignarId, usuarios]);

  // Refs (scroll horizontal arriba + contenedor real)
  const tableroRef = useRef(null);
  const scrollTopRef = useRef(null);
  const syncingScrollRef = useRef(false);

  // =========================
  // Derivados (config lienzo)
  // =========================
  const COLS = Math.max(1, Number(configSIL.cols_canvas || 120));
  const FILAS = Math.max(1, Number(configSIL.filas_canvas || 40));
  const COL_PX = Math.max(20, Number(configSIL.col_px || 50));
  const ROW_HEIGHT = Math.max(20, Number(configSIL.row_height || 60));

  const GRID_WIDTH = COLS * COL_PX;
  const GRID_HEIGHT = FILAS * ROW_HEIGHT;

  // =========================
  // Helpers
  // =========================
  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const setErr = (msg) => setError(msg || "");

  const normalizarNodo = (n) => {
    const hasLayout = n.x !== null && n.x !== undefined && n.y !== null && n.y !== undefined;

    return {
      ...n,
      id: Number(n.id),
      titulo: n.titulo || "",
      etiqueta: n.etiqueta || "",
      x: hasLayout ? Number(n.x) : null,
      y: hasLayout ? Number(n.y) : null,
      w: W_FIJO,
      h: H_FIJO,
      enPanel: hasLayout,
    };
  };

  const nodosPrincipal = useMemo(() => (nodos || []).filter((n) => !!n.enPanel), [nodos]);
  const nodosBanco = useMemo(() => (nodos || []).filter((n) => !n.enPanel), [nodos]);

  const layoutPrincipal = useMemo(() => {
    return nodosPrincipal.map((n) => ({
      i: String(n.id),
      x: Number.isFinite(Number(n.x)) ? Number(n.x) : 0,
      y: Number.isFinite(Number(n.y)) ? Number(n.y) : 0,
      w: W_FIJO,
      h: H_FIJO,
    }));
  }, [nodosPrincipal]);

  // Ausencias map
  const ausenciasMap = useMemo(() => {
    const m = new Map();
    (ausenciasActivas || []).forEach((a) => m.set(Number(a.nodo_id), a));
    return m;
  }, [ausenciasActivas]);

  const isNodoAusente = (nodoId) => ausenciasMap.has(Number(nodoId));

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString();
  };

  // =========================
  // Fetch inicial
  // =========================
  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      setErrorLienzo(null);

      try {
        await Promise.all([fetchConfigSIL(), fetchUsuarios(), fetchOrganigrama(), fetchAusenciasActivas()]);
      } catch (e) {
        console.error(e);
        setErr("No se pudieron cargar datos.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const fetchOrganigrama = async () => {
    try {
      const res = await fetch(`${Global.url}nodos/organigrama`, { headers: authHeaders() });
      if (!res.ok) {
        setErr(`Error al obtener organigrama (HTTP ${res.status})`);
        return;
      }
      const data = await res.json().catch(() => []);
      const lista = Array.isArray(data) ? data : data?.data || data?.nodos || [];
      setNodos((lista || []).map(normalizarNodo));
      setLayoutDirty(false);
    } catch (e) {
      console.error("fetchOrganigrama:", e);
      setErr("Error de red al obtener organigrama");
    }
  };

  const fetchUsuarios = async () => {
    try {
      const res = await fetch(`${Global.url}usuarios/todos`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json().catch(() => []);
      setUsuarios(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("fetchUsuarios:", e);
    }
  };

  const fetchConfigSIL = async () => {
    try {
      const res = await fetch(`${Global.url}nodos/config`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json().catch(() => ({}));
      if (data?.cols_canvas != null) {
        setConfigSIL({
          cols_canvas: Number(data.cols_canvas || 120),
          filas_canvas: Number(data.filas_canvas || 40),
          col_px: Number(data.col_px || 50),
          row_height: Number(data.row_height || 60),
        });
        setConfigDirty(false);
      }
    } catch (e) {
      console.error("fetchConfigSIL:", e);
    }
  };

  const fetchAusenciasActivas = async () => {
    setAusenciasLoading(true);
    try {
      const res = await fetch(`${Global.url}nodos/ausencias/activas`, { headers: authHeaders() });
      const data = await res.json().catch(() => []);
      if (!res.ok) return;
      setAusenciasActivas(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("fetchAusenciasActivas:", e);
    } finally {
      setAusenciasLoading(false);
    }
  };

  // =========================
  // Guardar config con 409
  // =========================
  const guardarConfigSIL = async () => {
    setErr("");
    setErrorLienzo(null);

    try {
      const res = await fetch(`${Global.url}nodos/config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(configSIL),
      });

      const body = await res.json().catch(() => ({}));

      if (res.status === 409) {
        setErrorLienzo({
          message: body?.message || "El nuevo tamaño del lienzo no permite el layout actual",
          detalle: body?.detalle || null,
        });
        return;
      }

      if (!res.ok) {
        setErr(body?.message || "Error guardando configuración del lienzo");
        return;
      }

      setConfigDirty(false);
      await fetchConfigSIL();
      toast.success("Configuración del lienzo guardada.");
    } catch (e) {
      console.error(e);
      setErr("Error de red guardando configuración del lienzo");
    }
  };

  const ajustarLienzoAMinimoValido = () => {
    if (!errorLienzo?.detalle) return;
    const minCols = Number(errorLienzo.detalle.requerido_min_cols || 0);
    const minFilas = Number(errorLienzo.detalle.requerido_min_filas || 0);

    setConfigSIL((p) => ({
      ...p,
      cols_canvas: Math.max(Number(p.cols_canvas || 1), minCols),
      filas_canvas: Math.max(Number(p.filas_canvas || 1), minFilas),
    }));
    setConfigDirty(true);
    setErrorLienzo(null);
  };

  // =========================
  // Layout: drag
  // =========================
  const onLayoutChange = (newLayout) => {
    setNodos((prev) => {
      const map = new Map((prev || []).map((n) => [String(n.id), { ...n }]));
      (newLayout || []).forEach((it) => {
        const id = String(it.i);
        const n = map.get(id);
        if (!n) return;
        if (!n.enPanel) return;

        n.x = Number(it.x) || 0;
        n.y = Number(it.y) || 0;
        n.w = W_FIJO;
        n.h = H_FIJO;

        map.set(id, n);
      });
      return Array.from(map.values());
    });
    setLayoutDirty(true);
  };

  const guardarLayout = async () => {
    setErr("");
    try {
      const items = nodosPrincipal.map((n) => ({
        nodoId: n.id,
        x: Number(n.x) || 0,
        y: Number(n.y) || 0,
        w: W_FIJO,
        h: H_FIJO,
      }));

      const res = await fetch(`${Global.url}nodos/layout/guardar`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ items }),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(body?.message || `Error guardando layout (HTTP ${res.status})`);
        return;
      }

      setLayoutDirty(false);
      toast.success("Layout guardado.");
      await fetchOrganigrama();
    } catch (e) {
      console.error(e);
      setErr("Error de red guardando layout");
    }
  };

  const quitarDelPanel = async (id) => {
    setErr("");
    try {
      const res = await fetch(`${Global.url}nodos/${id}/layout`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(body?.message || `Error quitando del panel (HTTP ${res.status})`);
        return;
      }
      await fetchOrganigrama();
    } catch (e) {
      console.error(e);
      setErr("Error de red quitando del panel");
    }
  };

  const ponerEnPanel = async (nodo) => {
    setErr("");
    try {
      const res = await fetch(`${Global.url}nodos/layout/guardar`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          items: [{ nodoId: nodo.id, x: 0, y: 0, w: W_FIJO, h: H_FIJO }],
        }),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(body?.message || `Error poniendo en panel (HTTP ${res.status})`);
        return;
      }

      await fetchOrganigrama();
    } catch (e) {
      console.error(e);
      setErr("Error de red poniendo en panel");
    }
  };

  // =========================
  // AUSENCIAS (admin)
  // =========================
  const activarAusencia = async (nodo) => {
    if (!nodo?.id) return;

    const motivo = window.prompt("Motivo (opcional):", "") || "";

    setErr("");
    try {
      const res = await fetch(`${Global.url}nodos/${nodo.id}/ausencia/on`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ motivo: motivo.trim() || null }),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(body?.message || `Error activando ausencia (HTTP ${res.status})`);
        return;
      }

      await fetchAusenciasActivas();
    } catch (e) {
      console.error(e);
      setErr("Error de red activando ausencia");
    }
  };

  const desactivarAusencia = async (nodo) => {
    if (!nodo?.id) return;

    if (!window.confirm("¿Desactivar ausencia para este nodo?")) return;

    setErr("");
    try {
      const res = await fetch(`${Global.url}nodos/${nodo.id}/ausencia/off`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({}),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(body?.message || `Error desactivando ausencia (HTTP ${res.status})`);
        return;
      }

      await fetchAusenciasActivas();
    } catch (e) {
      console.error(e);
      setErr("Error de red desactivando ausencia");
    }
  };

  // =========================
  // Scroll horizontal arriba (sync)
  // =========================
  const onScrollTopBar = (e) => {
    if (!tableroRef.current) return;
    if (syncingScrollRef.current) return;
    syncingScrollRef.current = true;
    tableroRef.current.scrollLeft = e.target.scrollLeft;
    requestAnimationFrame(() => {
      syncingScrollRef.current = false;
    });
  };

  const onScrollTablero = (e) => {
    if (!scrollTopRef.current) return;
    if (syncingScrollRef.current) return;
    syncingScrollRef.current = true;
    scrollTopRef.current.scrollLeft = e.target.scrollLeft;
    requestAnimationFrame(() => {
      syncingScrollRef.current = false;
    });
  };

  // =========================
  // CRUD nodo
  // =========================
  const abrirCrearNodo = () => {
    setFormTitulo("");
    setFormEtiqueta("");
    setNodoSeleccionado(null);
    setErr("");
    setDlgCrear(true);
  };

  const crearNodo = async () => {
    setErr("");
    if (!formTitulo.trim()) {
      setErr("El título es obligatorio");
      return;
    }
    try {
      const res = await fetch(`${Global.url}nodos/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          titulo: formTitulo.trim(),
          etiqueta: formEtiqueta.trim() || null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(body?.message || `Error creando nodo (HTTP ${res.status})`);
        return;
      }
      setDlgCrear(false);
      await fetchOrganigrama();
    } catch (e) {
      console.error(e);
      setErr("Error de red creando nodo");
    }
  };

  const abrirEditarNodo = (nodo) => {
    setNodoSeleccionado(nodo);
    setFormTitulo(nodo.titulo || "");
    setFormEtiqueta(nodo.etiqueta || "");
    setUsuarioAsignarId(nodo?.usuario_id ? String(nodo.usuario_id) : "");
    setErr("");
    setDlgEditar(true);
  };

  const editarNodo = async () => {
    if (!nodoSeleccionado?.id) return;
    setErr("");
    if (!formTitulo.trim()) {
      setErr("El título es obligatorio");
      return;
    }
    try {
      const res = await fetch(`${Global.url}nodos/${nodoSeleccionado.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          titulo: formTitulo.trim(),
          etiqueta: formEtiqueta.trim() || null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(body?.message || `Error editando nodo (HTTP ${res.status})`);
        return;
      }
      setDlgEditar(false);
      setNodoSeleccionado(null);
      await fetchOrganigrama();
    } catch (e) {
      console.error(e);
      setErr("Error de red editando nodo");
    }
  };

  const eliminarNodo = async (id) => {
    if (!window.confirm("¿Eliminar este nodo?")) return;
    setErr("");
    try {
      const res = await fetch(`${Global.url}nodos/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(body?.message || `Error eliminando nodo (HTTP ${res.status})`);
        return;
      }
      await fetchOrganigrama();
      await fetchAusenciasActivas();
    } catch (e) {
      console.error(e);
      setErr("Error de red eliminando nodo");
    }
  };

  // =========================
  // Asignación usuario
  // =========================
  const abrirAsignarUsuario = (nodo) => {
    setNodoSeleccionado(nodo);
    setUsuarioAsignarId(nodo?.usuario_id ? String(nodo.usuario_id) : "");
    setErr("");
    setDlgAsignar(true);
  };

  const asignarUsuario = async () => {
    if (!nodoSeleccionado?.id) return;
    setErr("");
    try {
      const res = await fetch(`${Global.url}nodos/${nodoSeleccionado.id}/asignar-usuario`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          usuario_id: usuarioAsignarId ? Number(usuarioAsignarId) : null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(body?.message || `Error asignando usuario (HTTP ${res.status})`);
        return;
      }
      setDlgAsignar(false);
      setNodoSeleccionado(null);
      await fetchOrganigrama();
    } catch (e) {
      console.error(e);
      setErr("Error de red asignando usuario");
    }
  };

  const quitarUsuario = async (nodo) => {
    if (!window.confirm("¿Quitar usuario de este nodo?")) return;
    setErr("");
    try {
      const res = await fetch(`${Global.url}nodos/${nodo.id}/quitar-usuario`, {
        method: "PUT",
        headers: authHeaders(),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(body?.message || `Error quitando usuario (HTTP ${res.status})`);
        return;
      }
      await fetchOrganigrama();
    } catch (e) {
      console.error(e);
      setErr("Error de red quitando usuario");
    }
  };

  // =========================
  // Render tarjeta
  // =========================
  const renderNodoCard = (nodo) => {
    const tieneUsuario = !!nodo?.usuario_id;
    const nombreUsuario = tieneUsuario ? `${nodo.nombre || ""} ${nodo.apellidos || ""}`.trim() : "Sin asignar";

    const ausente = isNodoAusente(nodo.id);
    const ausencia = ausenciasMap.get(Number(nodo.id));
    const tooltipAusencia = ausencia
      ? `Ausente desde: ${formatDate(ausencia.desde)}${ausencia.motivo ? `\nMotivo: ${ausencia.motivo}` : ""}`
      : "";

    return (
      <div className="sil-nodo-card-inner">
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
          <div className="sil-nodo-titulo">{nodo.titulo}</div>

          {ausente && (
            <span
              title={tooltipAusencia}
              style={{
                fontSize: 12,
                fontWeight: 800,
                padding: "4px 8px",
                borderRadius: 999,
                background: "rgba(241,196,15,.18)",
                border: "1px solid rgba(241,196,15,.45)",
                whiteSpace: "nowrap",
                alignSelf: "flex-start",
              }}
            >
              <DirectionsWalkIcon sx={{ fontSize: 16, verticalAlign: "text-bottom" }} /> AUSENTE
            </span>
          )}
        </div>

        <div className={`sil-nodo-usuario ${tieneUsuario ? "" : "muted"}`}>
          {nombreUsuario}
          {nodo.etiqueta ? ` ${nodo.etiqueta}` : ""}
        </div>

        <div className="sil-nodo-acciones">
          <button className="btn-mapa" onClick={() => abrirEditarNodo(nodo)} title="Editar">
            <EditIcon fontSize="small" />
          </button>

          <button className="btn-mapa" onClick={() => abrirAsignarUsuario(nodo)} title="Asignar usuario">
            <PersonAddIcon fontSize="small" />
          </button>

          {ausente ? (
            <button className="btn-mapa" onClick={() => desactivarAusencia(nodo)} title="Marcar presente (quitar ausencia)">
              <HowToRegIcon fontSize="small" />
            </button>
          ) : (
            <button className="btn-mapa" onClick={() => activarAusencia(nodo)} title="Marcar ausente">
              <DirectionsWalkIcon fontSize="small" />
            </button>
          )}

          <button className="btn-mapa" onClick={() => quitarDelPanel(nodo.id)} title="Quitar del panel">
            <LogoutIcon fontSize="small" />
          </button>
        </div>
      </div>
    );
  };

  // =========================
  // Render
  // =========================
  if (loading) {
    return (
      <Box className="appContainer appContainer--wide">
        <Stack spacing={2} sx={{ alignItems: "flex-start" }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <CircularProgress size={20} />
            <Typography>Cargando…</Typography>
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        {/* Header */}
        <Box className="pageHeader">
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            SIL Control · Editor de Nodos
          </Typography>
        </Box>

        {/* Toolbar */}
        <Paper sx={{ p: 1.5 }}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={1}
            sx={{ alignItems: { lg: "center" }, justifyContent: "space-between", gap: 1 }}
          >
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              <Button size="small" startIcon={<AddIcon />} onClick={abrirCrearNodo}>
                Crear nodo
              </Button>

              <Button size="small" startIcon={<SaveIcon />} onClick={guardarLayout}>
                Guardar layout{layoutDirty ? " *" : ""}
              </Button>

              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={async () => {
                  await fetchOrganigrama();
                  await fetchAusenciasActivas();
                }}
              >
                Recargar
              </Button>

              <Button
                size="small"
                startIcon={<DirectionsWalkIcon />}
                onClick={fetchAusenciasActivas}
                disabled={ausenciasLoading}
              >
                Ausencias{ausenciasLoading ? "..." : ""}
              </Button>
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ alignItems: { md: "center" } }}>
              <Typography sx={{ fontWeight: 700, mr: 1 }}>Lienzo</Typography>

              <TextField
                label="Columnas"
                type="number"
                size="small"
                value={configSIL.cols_canvas}
                onChange={(e) => {
                  setConfigSIL((p) => ({ ...p, cols_canvas: Number(e.target.value || 1) }));
                  setConfigDirty(true);
                }}
                inputProps={{ min: 1, max: 1000 }}
                sx={{ width: 140 }}
              />

              <TextField
                label="Filas"
                type="number"
                size="small"
                value={configSIL.filas_canvas}
                onChange={(e) => {
                  setConfigSIL((p) => ({ ...p, filas_canvas: Number(e.target.value || 1) }));
                  setConfigDirty(true);
                }}
                inputProps={{ min: 1, max: 1000 }}
                sx={{ width: 140 }}
              />

              <TextField
                label="Ancho (px)"
                type="number"
                size="small"
                value={configSIL.col_px}
                onChange={(e) => {
                  setConfigSIL((p) => ({ ...p, col_px: Number(e.target.value || 50) }));
                  setConfigDirty(true);
                }}
                inputProps={{ min: 20, max: 2000 }}
                sx={{ width: 140 }}
              />

              <TextField
                label="Alto fila"
                type="number"
                size="small"
                value={configSIL.row_height}
                onChange={(e) => {
                  setConfigSIL((p) => ({ ...p, row_height: Number(e.target.value || 60) }));
                  setConfigDirty(true);
                }}
                inputProps={{ min: 20, max: 400 }}
                sx={{ width: 140 }}
              />

              <Button size="small" startIcon={<SettingsIcon />} onClick={guardarConfigSIL}>
                Guardar{configDirty ? " *" : ""}
              </Button>
            </Stack>
          </Stack>

          {error ? (
            <Box sx={{ mt: 1 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          ) : null}
        </Paper>

        {/* Error lienzo (409) */}
        {errorLienzo ? (
          <Alert
            severity="error"
            sx={{ whiteSpace: "pre-wrap" }}
            action={
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                {errorLienzo?.detalle ? (
                  <Button size="small" variant="outlined" onClick={ajustarLienzoAMinimoValido}>
                    Ajustar mínimo válido
                  </Button>
                ) : null}
                <Button size="small" onClick={() => setErrorLienzo(null)}>
                  Cerrar
                </Button>
              </Stack>
            }
          >
            <Typography sx={{ fontWeight: 700 }}>{errorLienzo.message}</Typography>
            {errorLienzo.detalle ? (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                El nodo #{errorLienzo.detalle.nodo_id} necesita al menos {errorLienzo.detalle.requerido_min_cols} columnas y{" "}
                {errorLienzo.detalle.requerido_min_filas} filas.
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Reduce menos el lienzo o reubica los nodos antes de guardar.
              </Typography>
            )}
          </Alert>
        ) : null}

        {/* Banco + Panel */}
        <Stack direction={{ xs: "column", lg: "row" }} spacing={2} sx={{ alignItems: "flex-start" }}>
          {/* Banco */}
          <Box sx={{ width: { xs: "100%", lg: 360 }, flexShrink: 0 }}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Banco (sin colocar)</Typography>

            {nodosBanco.length === 0 ? (
              <Typography color="text.secondary">No hay nodos en el banco.</Typography>
            ) : (
              <Stack spacing={1.5}>
                {nodosBanco.map((n) => {
                  const tieneUsuario = !!n?.usuario_id;
                  const nombreUsuario = tieneUsuario
                    ? `${n.nombre || ""} ${n.apellidos || ""}`.trim()
                    : "Sin asignar";

                  const ausente = isNodoAusente(n.id);
                  const ausencia = ausenciasMap.get(Number(n.id));
                  const tooltipAusencia = ausencia
                    ? `Ausente desde: ${formatDate(ausencia.desde)}${
                        ausencia.motivo ? `\nMotivo: ${ausencia.motivo}` : ""
                      }`
                    : "";

                  return (
                    <Paper key={n.id} className="sil-nodo-card" sx={{ p: 1.5 }}>
                      <Stack spacing={1}>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: "flex-start", justifyContent: "space-between" }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>
                              {n.titulo}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{ mt: 0.5 }}
                              color={tieneUsuario ? "text.primary" : "text.secondary"}
                            >
                              {nombreUsuario}
                              {n.etiqueta ? ` · ${n.etiqueta}` : ""}
                            </Typography>
                          </Box>

                          {ausente ? (
                            <Box
                              title={tooltipAusencia}
                              sx={{
                                fontSize: 12,
                                fontWeight: 800,
                                px: 1,
                                py: 0.5,
                                borderRadius: 999,
                                background: "rgba(241,196,15,.18)",
                                border: "1px solid rgba(241,196,15,.45)",
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                              }}
                            >
                              <DirectionsWalkIcon sx={{ fontSize: 16, verticalAlign: "text-bottom" }} /> AUSENTE
                            </Box>
                          ) : null}
                        </Stack>

                        {/* Acciones: 2 columnas, botones fullWidth */}
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                            gap: 1,
                          }}
                        >
                          <Button
                            size="small"
                            variant="contained"
                            fullWidth
                            startIcon={<EditIcon />}
                            onClick={() => abrirEditarNodo(n)}
                          >
                            Editar
                          </Button>

                          <Button
                            size="small"
                            variant="contained"
                            fullWidth
                            startIcon={<PersonAddIcon />}
                            onClick={() => abrirAsignarUsuario(n)}
                          >
                            Asignar
                          </Button>

                          {ausente ? (
                            <Button
                              size="small"
                              variant="contained"
                              fullWidth
                              startIcon={<HowToRegIcon />}
                              onClick={() => desactivarAusencia(n)}
                            >
                              Presente
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="contained"
                              fullWidth
                              startIcon={<DirectionsWalkIcon />}
                              onClick={() => activarAusencia(n)}
                            >
                              Ausente
                            </Button>
                          )}

                          <Button
                            size="small"
                            variant="contained"
                            fullWidth
                            startIcon={<LoginIcon />}
                            onClick={() => ponerEnPanel(n)}
                          >
                            Poner en panel
                          </Button>

                          {tieneUsuario ? (
                            <Button
                              size="small"
                              variant="outlined"
                              fullWidth
                              startIcon={<PersonRemoveIcon />}
                              onClick={() => quitarUsuario(n)}
                            >
                              Quitar usuario
                            </Button>
                          ) : (
                            <Box />
                          )}

                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            fullWidth
                            startIcon={<DeleteIcon />}
                            onClick={() => eliminarNodo(n.id)}
                          >
                            Eliminar
                          </Button>
                        </Box>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Box>

          {/* Panel principal */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Zona principal</Typography>

            {/* Scroll horizontal superior */}
            <Box
              ref={scrollTopRef}
              onScroll={onScrollTopBar}
              sx={{
                height: 16,
                overflowX: "auto",
                overflowY: "hidden",
                mb: 1,
                borderRadius: 2,
              }}
            >
              <Box sx={{ width: GRID_WIDTH, height: 1 }} />
            </Box>

            <Paper
              ref={tableroRef}
              onScroll={onScrollTablero}
              sx={{
                height: "calc(100vh - 320px)",
                overflow: "auto",
                borderRadius: 2,
                border: "1px solid rgba(0,0,0,.06)",
                backgroundColor: "#fff",
              }}
            >
              <Box sx={{ width: GRID_WIDTH, height: GRID_HEIGHT }}>
                <GridLayout
                  layout={layoutPrincipal}
                  cols={COLS}
                  rowHeight={ROW_HEIGHT}
                  width={GRID_WIDTH}
                  maxRows={FILAS}
                  margin={[12, 12]}
                  containerPadding={[12, 12]}
                  isResizable={false}
                  isDraggable
                  preventCollision={false}
                  compactType={null}
                  onLayoutChange={onLayoutChange}
                >
                  {nodosPrincipal.map((n) => (
                    <div key={String(n.id)} className="sil-nodo-card">
                      {renderNodoCard(n)}
                    </div>
                  ))}
                </GridLayout>
              </Box>
            </Paper>
          </Box>
        </Stack>

        {/* =========================
            Dialog: Crear nodo
           ========================= */}
        <Dialog open={dlgCrear} onClose={() => setDlgCrear(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            Crear nodo
            <IconButton aria-label="Cerrar" onClick={() => setDlgCrear(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <TextField
                label="Título"
                value={formTitulo}
                onChange={(e) => setFormTitulo(e.target.value)}
                required
                autoFocus
              />
              <TextField
                label="Etiqueta (opcional)"
                value={formEtiqueta}
                onChange={(e) => setFormEtiqueta(e.target.value)}
              />

              {error ? <Alert severity="error">{error}</Alert> : null}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={() => setDlgCrear(false)}>
              Cancelar
            </Button>
            <Button onClick={crearNodo} startIcon={<AddIcon />}>
              Crear
            </Button>
          </DialogActions>
        </Dialog>

        {/* =========================
            Dialog: Editar nodo
           ========================= */}
        <Dialog open={dlgEditar} onClose={() => setDlgEditar(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            Editar nodo
            <IconButton aria-label="Cerrar" onClick={() => setDlgEditar(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <TextField
                label="Título"
                value={formTitulo}
                onChange={(e) => setFormTitulo(e.target.value)}
                required
                autoFocus
              />
              <TextField
                label="Etiqueta (opcional)"
                value={formEtiqueta}
                onChange={(e) => setFormEtiqueta(e.target.value)}
              />

              {error ? <Alert severity="error">{error}</Alert> : null}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={() => setDlgEditar(false)}>
              Cancelar
            </Button>
            <Button onClick={editarNodo} startIcon={<SaveIcon />}>
              Guardar
            </Button>
          </DialogActions>
        </Dialog>

        {/* =========================
            Dialog: Asignar usuario
           ========================= */}
        <Dialog open={dlgAsignar} onClose={() => setDlgAsignar(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            Asignar usuario
            <IconButton aria-label="Cerrar" onClick={() => setDlgAsignar(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <Typography sx={{ fontWeight: 700 }}>
                Nodo: {nodoSeleccionado?.titulo || "—"}
              </Typography>

              <Autocomplete
                options={usuarios || []}
                value={usuarioAsignarObj}
                onChange={(_, newValue) => setUsuarioAsignarId(newValue ? String(newValue.id) : "")}
                getOptionLabel={(u) =>
                  `${u?.nombre || ""} ${u?.apellidos || ""} (${u?.email || u?.dni || "sin id"})`.trim()
                }
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                renderInput={(params) => (
                  <TextField {...params} label="Usuario" placeholder="Buscar usuario…" />
                )}
                clearOnEscape
              />

              <FormControl fullWidth>
                <InputLabel id="sin-asignar-label">Opciones</InputLabel>
                <Select
                  labelId="sin-asignar-label"
                  label="Opciones"
                  value={usuarioAsignarId ? "asignado" : "sin_asignar"}
                  onChange={(e) => {
                    if (e.target.value === "sin_asignar") setUsuarioAsignarId("");
                  }}
                >
                  <MenuItem value="asignado">Asignado (selecciona arriba)</MenuItem>
                  <MenuItem value="sin_asignar">Sin asignar</MenuItem>
                </Select>
              </FormControl>

              {error ? <Alert severity="error">{error}</Alert> : null}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={() => setDlgAsignar(false)}>
              Cancelar
            </Button>
            <Button onClick={asignarUsuario} startIcon={<SaveIcon />}>
              Guardar
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};

export default GestionNodoSIL;
